import { useState } from 'react';
import { cocCategories, cocImpactedElements } from '../../services/mockData';

const COCRequestForm = ({ loan, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    category: '',
    description: '',
    discoveryDate: '',
    reasonNotKnown: '',
    impactedElements: [],
    supportingDocuments: [],
    attestation: false
  });

  const [errors, setErrors] = useState({});
  const [uploadingFiles, setUploadingFiles] = useState(false);

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData.category) {
          newErrors.category = 'COC category is required';
        }
        break;
      case 2:
        if (!formData.description || formData.description.trim().length < 50) {
          newErrors.description = 'Description must be at least 50 characters and answer all required questions';
        }
        if (!formData.discoveryDate) {
          newErrors.discoveryDate = 'Discovery date is required';
        }
        if (!formData.reasonNotKnown || formData.reasonNotKnown.trim().length < 30) {
          newErrors.reasonNotKnown = 'Explanation must be at least 30 characters';
        }
        break;
      case 3:
        if (formData.impactedElements.length === 0) {
          newErrors.impactedElements = 'Select at least one impacted element';
        }
        break;
      case 4:
        if (formData.supportingDocuments.length === 0) {
          newErrors.supportingDocuments = 'At least one supporting document is required';
        }
        break;
      case 5:
        if (!formData.attestation) {
          newErrors.attestation = 'Attestation is required to submit';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateAll = () => {
    return validateStep(1) && validateStep(2) && validateStep(3) && validateStep(4) && validateStep(5);
  };

  const handleCategoryChange = (categoryId) => {
    setFormData({ ...formData, category: categoryId });
    setErrors({ ...errors, category: '' });
  };

  const handleImpactedElementToggle = (elementId) => {
    const current = formData.impactedElements;
    const updated = current.includes(elementId)
      ? current.filter(id => id !== elementId)
      : [...current, elementId];
    
    setFormData({ ...formData, impactedElements: updated });
    setErrors({ ...errors, impactedElements: '' });
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingFiles(true);

    // Simulate file upload
    setTimeout(() => {
      const newDocs = files.map((file, idx) => ({
        id: Date.now() + idx,
        fileName: file.name,
        fileSize: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
        uploadDate: new Date().toISOString(),
        label: ''
      }));

      setFormData({
        ...formData,
        supportingDocuments: [...formData.supportingDocuments, ...newDocs]
      });
      setErrors({ ...errors, supportingDocuments: '' });
      setUploadingFiles(false);
      e.target.value = '';
    }, 500);
  };

  const handleDocumentLabelChange = (docId, label) => {
    setFormData({
      ...formData,
      supportingDocuments: formData.supportingDocuments.map(doc =>
        doc.id === docId ? { ...doc, label } : doc
      )
    });
  };

  const handleRemoveDocument = (docId) => {
    setFormData({
      ...formData,
      supportingDocuments: formData.supportingDocuments.filter(doc => doc.id !== docId)
    });
  };

  const handleSubmit = () => {
    if (!validateAll()) {
      alert('Please complete all required fields before submitting.');
      return;
    }

    const selectedCategory = cocCategories.find(cat => cat.id === formData.category);
    
    const cocRequest = {
      id: Date.now(),
      loanId: loan.id,
      loanNumber: loan.loanNumber,
      category: formData.category,
      categoryName: selectedCategory.name,
      description: formData.description,
      discoveryDate: formData.discoveryDate,
      reasonNotKnown: formData.reasonNotKnown,
      impactedElements: formData.impactedElements,
      supportingDocuments: formData.supportingDocuments,
      attestation: formData.attestation,
      attestationText: 'I attest that this change represents a valid Change of Circumstance under TRID regulations, was not known or reasonably foreseeable at the time of initial disclosure, and is supported by the attached documentation.',
      submittedBy: 'Michael Chen', // Would come from auth context
      submittedDate: new Date().toISOString(),
      status: 'Submitted',
      reviewedBy: null,
      reviewedDate: null,
      reviewNotes: null,
      revisedDisclosureIssued: false,
      revisedDisclosureDate: null
    };

    onSubmit(cocRequest);
  };

  const selectedCategory = cocCategories.find(cat => cat.id === formData.category);

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Request Change of Circumstance</h3>
        <div className="card-subtitle text-secondary mt-1">
          Submit a TRID-compliant COC request for lender review
        </div>
      </div>
      <div className="card-body">
        {/* Compliance Notice */}
        <div className="alert alert-info mb-4">
          <div className="d-flex">
            <div>
              <i className="ti ti-info-circle icon alert-icon"></i>
            </div>
            <div>
              <h4 className="alert-title">TRID Compliance Notice</h4>
              <div className="text-secondary">
                Changes of Circumstance must be valid under TRID regulations. Invalid COC types (pricing errors, 
                broker compensation changes, margin adjustments) are not permitted. All requests require supporting 
                documentation and will be reviewed for compliance.
              </div>
            </div>
          </div>
        </div>

        {/* Step 1: Select COC Category */}
        <div className="mb-4">
          <h4 className="mb-3">
            <span className="badge bg-primary me-2">1</span>
            Select COC Category
            <span className="text-danger ms-1">*</span>
          </h4>
          
          <div className="row g-3">
            {cocCategories.map(category => (
              <div key={category.id} className="col-md-6">
                <label className={`form-selectgroup-item ${formData.category === category.id ? 'border-primary' : ''}`}>
                  <input
                    type="radio"
                    name="coc-category"
                    value={category.id}
                    className="form-selectgroup-input"
                    checked={formData.category === category.id}
                    onChange={() => handleCategoryChange(category.id)}
                  />
                  <span className="form-selectgroup-label">
                    <div className="fw-bold mb-1">{category.name}</div>
                    <div className="text-secondary small mb-2">{category.description}</div>
                    <div className="text-muted small">
                      <i className="ti ti-shield-check me-1"></i>
                      {category.complianceNote}
                    </div>
                  </span>
                </label>
              </div>
            ))}
          </div>
          
          {errors.category && (
            <div className="invalid-feedback d-block">{errors.category}</div>
          )}
        </div>

        {/* Step 2: Change Description */}
        {formData.category && (
          <div className="mb-4">
            <h4 className="mb-3">
              <span className="badge bg-primary me-2">2</span>
              Change Description
              <span className="text-danger ms-1">*</span>
            </h4>

            <div className="alert alert-azure mb-3">
              <div className="text-secondary small">
                <strong>Required Information:</strong> Your description must clearly answer:
                <ul className="mb-0 mt-2">
                  <li>What changed?</li>
                  <li>When was it discovered?</li>
                  <li>Why was it not known at initial disclosure?</li>
                </ul>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">
                Detailed Description
                <span className="text-danger ms-1">*</span>
              </label>
              <textarea
                className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                rows="5"
                placeholder="Provide a detailed explanation of what changed, when it was discovered, and why it was not known or reasonably foreseeable at the time of initial disclosure..."
                value={formData.description}
                onChange={(e) => {
                  setFormData({ ...formData, description: e.target.value });
                  setErrors({ ...errors, description: '' });
                }}
              />
              <div className="form-hint">
                {formData.description.length} characters (minimum 50 required)
              </div>
              {errors.description && (
                <div className="invalid-feedback">{errors.description}</div>
              )}
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Discovery Date
                  <span className="text-danger ms-1">*</span>
                </label>
                <input
                  type="date"
                  className={`form-control ${errors.discoveryDate ? 'is-invalid' : ''}`}
                  value={formData.discoveryDate}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) => {
                    setFormData({ ...formData, discoveryDate: e.target.value });
                    setErrors({ ...errors, discoveryDate: '' });
                  }}
                />
                {errors.discoveryDate && (
                  <div className="invalid-feedback">{errors.discoveryDate}</div>
                )}
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">
                Why was this not known at initial disclosure?
                <span className="text-danger ms-1">*</span>
              </label>
              <textarea
                className={`form-control ${errors.reasonNotKnown ? 'is-invalid' : ''}`}
                rows="3"
                placeholder="Explain why this change was not known or reasonably foreseeable at the time of initial disclosure..."
                value={formData.reasonNotKnown}
                onChange={(e) => {
                  setFormData({ ...formData, reasonNotKnown: e.target.value });
                  setErrors({ ...errors, reasonNotKnown: '' });
                }}
              />
              <div className="form-hint">
                {formData.reasonNotKnown.length} characters (minimum 30 required)
              </div>
              {errors.reasonNotKnown && (
                <div className="invalid-feedback">{errors.reasonNotKnown}</div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Impacted Loan Elements */}
        {formData.category && formData.description && (
          <div className="mb-4">
            <h4 className="mb-3">
              <span className="badge bg-primary me-2">3</span>
              Impacted Loan Elements
              <span className="text-danger ms-1">*</span>
            </h4>

            <div className="form-selectgroup form-selectgroup-boxes">
              {cocImpactedElements.map(element => (
                <label key={element.id} className="form-selectgroup-item flex-fill">
                  <input
                    type="checkbox"
                    className="form-selectgroup-input"
                    checked={formData.impactedElements.includes(element.id)}
                    onChange={() => handleImpactedElementToggle(element.id)}
                  />
                  <span className="form-selectgroup-label">
                    <i className="ti ti-check me-2"></i>
                    {element.name}
                  </span>
                </label>
              ))}
            </div>

            {errors.impactedElements && (
              <div className="invalid-feedback d-block">{errors.impactedElements}</div>
            )}
          </div>
        )}

        {/* Step 4: Supporting Documentation */}
        {formData.category && formData.impactedElements.length > 0 && (
          <div className="mb-4">
            <h4 className="mb-3">
              <span className="badge bg-primary me-2">4</span>
              Supporting Documentation
              <span className="text-danger ms-1">*</span>
            </h4>

            <div className="alert alert-warning mb-3">
              <div className="d-flex">
                <div>
                  <i className="ti ti-alert-triangle icon alert-icon"></i>
                </div>
                <div className="text-secondary">
                  All COC requests must include supporting documentation. Upload relevant documents such as 
                  appraisals, borrower communications, fee invoices, or other evidence supporting this change.
                </div>
              </div>
            </div>

            <div className="mb-3">
              <label className="btn btn-primary">
                <i className="ti ti-upload me-2"></i>
                Upload Documents
                <input
                  type="file"
                  className="d-none"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={handleFileUpload}
                  disabled={uploadingFiles}
                />
              </label>
              {uploadingFiles && (
                <span className="ms-2 text-secondary">
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Uploading...
                </span>
              )}
            </div>

            {formData.supportingDocuments.length > 0 && (
              <div className="list-group">
                {formData.supportingDocuments.map(doc => (
                  <div key={doc.id} className="list-group-item">
                    <div className="row align-items-center">
                      <div className="col-auto">
                        <i className="ti ti-file-text text-primary" style={{ fontSize: '2rem' }}></i>
                      </div>
                      <div className="col">
                        <div className="fw-bold">{doc.fileName}</div>
                        <div className="text-secondary small">{doc.fileSize}</div>
                      </div>
                      <div className="col-md-4">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Document label (e.g., Appraisal, Invoice)"
                          value={doc.label}
                          onChange={(e) => handleDocumentLabelChange(doc.id, e.target.value)}
                        />
                      </div>
                      <div className="col-auto">
                        <button
                          className="btn btn-sm btn-ghost-danger"
                          onClick={() => handleRemoveDocument(doc.id)}
                        >
                          <i className="ti ti-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {errors.supportingDocuments && (
              <div className="invalid-feedback d-block">{errors.supportingDocuments}</div>
            )}
          </div>
        )}

        {/* Step 5: Broker Attestation */}
        {formData.category && formData.supportingDocuments.length > 0 && (
          <div className="mb-4">
            <h4 className="mb-3">
              <span className="badge bg-primary me-2">5</span>
              Broker Attestation
              <span className="text-danger ms-1">*</span>
            </h4>

            <div className="alert alert-danger">
              <div className="d-flex">
                <div>
                  <i className="ti ti-shield-check icon alert-icon"></i>
                </div>
                <div>
                  <h4 className="alert-title">Compliance Attestation Required</h4>
                  <div className="text-secondary">
                    By checking the box below, you are attesting under penalty of law that this COC request 
                    is valid, compliant with TRID regulations, and supported by the attached documentation.
                  </div>
                </div>
              </div>
            </div>

            <label className={`form-check ${errors.attestation ? 'is-invalid' : ''}`}>
              <input
                type="checkbox"
                className="form-check-input"
                checked={formData.attestation}
                onChange={(e) => {
                  setFormData({ ...formData, attestation: e.target.checked });
                  setErrors({ ...errors, attestation: '' });
                }}
              />
              <span className="form-check-label">
                <strong>I attest that this change represents a valid Change of Circumstance under TRID regulations, 
                was not known or reasonably foreseeable at the time of initial disclosure, and is supported by 
                the attached documentation.</strong>
              </span>
            </label>

            {errors.attestation && (
              <div className="invalid-feedback d-block">{errors.attestation}</div>
            )}
          </div>
        )}
      </div>

      <div className="card-footer">
        <div className="d-flex justify-content-between">
          <button className="btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={!formData.attestation}
          >
            <i className="ti ti-send me-2"></i>
            Submit COC Request
          </button>
        </div>
      </div>
    </div>
  );
};

export default COCRequestForm;
